import { AfterViewInit, Component, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Models } from '@ibexfusion/models';
import { CdkDrag } from '@angular/cdk/drag-drop';
import moment from 'moment';

@Component({
    selector: 'dynamic-content-table-row',
    templateUrl: './dynamic-content-table-row.component.html',
    styleUrls: ['./dynamic-content-table-row.component.scss'],
    standalone: false
})
export class DynamicContentTableRowComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() item: any;
  @Input() type: any;
  @Input() noCategories: any;
  @Input() forceCategories: any;
  @Input() highestOrder: any;
  @Input() lowestOrder: any;
  @Input() categories: any;
  @Input() connectedTo: any;
  @Input() selectedItems: any;
  @Input() searchValue: any;
  @Input() readOnly = false;
  @Input() dragRef!: CdkDrag;
  @Input() archive = false;
  @Input() showPreview = false;
  @Input() showOrder = false;
  @Input() app: Models.PackageInstalledApp;
  @Output() onSelect = new EventEmitter();
  @Output() onToggleChildren: EventEmitter<any> = new EventEmitter();
  @Output() onDetail = new EventEmitter();
  @Output() onEdit = new EventEmitter();
  @Output() onAddChild = new EventEmitter();
  @Output() onPreview = new EventEmitter();
  @Output() onDelete: EventEmitter<any> = new EventEmitter();

  resizeObserver!: ResizeObserver;
  noDragAndDropItems = ['activity', 'alphabet', 'event', 'gastronomy', 'person'];
  checked = false;

  medium = false;
  short = false;

  @HostBinding('class.expanded') expanded = false;

  @HostBinding('class') get levelClass() {
    return `level-${this.item.level + 1}`;
  }

  constructor(private el: ElementRef) {}

  ngOnInit() {
    if (this.item.showSub) {
      this.expanded = true;
    }
  }

  ngAfterViewInit() {
    this.resizeObserver = new ResizeObserver(entries => {
      this.medium = !!entries.find(entry => { return entry?.contentRect?.width < 770 } );
      this.short = !!entries.find(entry => { return entry?.contentRect?.width < 500 } );
    });
    if (this.el?.nativeElement?.parentElement) {
      this.resizeObserver.observe(this.el.nativeElement.parentElement);
    }
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
  }

  public toggleExpansion(event: any) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.expanded) {
      this.expand();
    } else {
      this.collapse();
    }
  }

  public expand(external = false) {
    this.expanded = true;
    if (!external) {
      this.onToggleChildren.emit(this.item);
    }
  }

  public collapse(external = false) {
    this.expanded = false;
    if (!external) {
      this.onToggleChildren.emit(this.item);
    }
  }

  public toggleSelect(event: any) {
    event.stopPropagation();
    if (this.checked) {
      this.unselect();
    } else {
      this.select();
    }
  }

  public select(external = false) {
    if (!external) {
      this.onSelect.emit(this.item);
    }
    this.checked = true;
  }

  public unselect(external = false) {
    if (!external) {
      this.onSelect.emit(this.item);
    }
    this.checked = false;
  }

  public detail(item: any, event: any) {
    if (event.stopPropagation) {
      event.stopPropagation();
    }
    if (event.preventDefault) {
      event.preventDefault();
    }
    this.onDetail.emit(item)
  }

  public edit(item: any, event: any) {
    if (event.stopPropagation) {
      event.stopPropagation();
    }
    if (event.preventDefault) {
      event.preventDefault();
    }
    this.onEdit.emit(item)
  }

  public delete(item: any, event: any) {
    if (event.stopPropagation) {
      event.stopPropagation();
    }
    if (event.preventDefault) {
      event.preventDefault();
    }
    this.onDelete.emit(item);
  }

  action(action: string, item: any, event: any) {
    event.stopPropagation?.();
    event.preventDefault?.();
    this['on' + action.charAt(0).toUpperCase() + action.slice(1)].emit();
  }

  hasAddChild() {
    const levels = { 'information': 2, 'gallery': 2, 'gastronomy': 1, 'feedback': 1, 'webcam': 1 };
    return levels[this.type] && levels[this.type] > this.item.level;
  }

  public getCategories() {
    return this.categories.filter(cat => (this.item?.data?.content?.categories || []).includes(cat.data?._id)).map(cat => cat.data?.title).filter(Boolean).join(', ');
  }

  public calculateNextExecution(content: any) {
    const todayMom = moment();
    if (this.type === 'activity') {
      if (content.repeating) {
        const mom = moment(content.date || undefined);
        switch (content.rhythm) {
          case 'daily':
            return todayMom.format('YYYY-MM-DD') + 'T00:00:00.000Z';
          case 'weekly':
            while (content.weekdays.indexOf(todayMom.weekday()) === -1) {
              todayMom.add(1, 'day');
            }
            return todayMom.format('YYYY-MM-DD') + 'T00:00:00.000Z';
          case 'monthly':
            if (mom.date() < todayMom.date()) {
              todayMom.add(1, 'month');
            }
            todayMom.date(mom.date());
            return todayMom.format('YYYY-MM-DD') + 'T00:00:00.000Z';
          case 'yearly':
            if (mom.isBefore(todayMom, 'day')) {
              todayMom.add(1, 'year');
            }
            todayMom.month(mom.month()).date(mom.date());
            return todayMom.format('YYYY-MM-DD') + 'T00:00:00.000Z';
        }
      }
      return content.date;
    } else if (this.type === 'event') {
      let eventDates;
      if (!content) {
        return false;
      }
      if (content?.repeating)  {
        let dates = this.getNextExecutionDateRepeating(content);
        const today = new Date();
        today.setHours(0,0,0,0);
        dates = this.archive ? dates.filter(date => date.getTime() <= today.getTime()).reverse() : dates.filter(date => date.getTime() >= today.getTime());
        // dates = dates.filter(date => date.getTime() >= today.getTime());
        return dates[0];
      }
      const { validity, multipleDates, dates } = content;
      if (validity && !multipleDates) {
        eventDates = (Array.isArray(dates) ? dates : [dates]);
      } else {
        eventDates = multipleDates || (Array.isArray(dates) ? dates : [dates]);
      }
      if (!eventDates.length) {
        return true;
      }
      if (!this.archive) {
        eventDates = eventDates.filter(date => {
          if (!date || date.daily === undefined) {
            return false;
          }
          const startDate = this.getDateUnix(date.startDate);
          const endDate = this.getDateUnix(date.endDate, true);
          return (!startDate && !endDate) || this.checkValidDate(startDate, endDate);
        });
      }
      return moment(this.getNextExecutionDate(eventDates)).format('YYYY-MM-DD') + 'T00:00:00.000Z';
    }
  }

  getNextExecutionDateRepeating(event: any) {
    let endDate = !!event?.repetition?.endDate ? new Date(event.repetition.endDate) : null;
    if (!endDate) {
      endDate = new Date();
      endDate.setFullYear(endDate.getFullYear() + 1);
    }
    if (this.app?.detail?.publicationTimespan) {
      let d = new Date();
      if (this.app.detail.publicationTimespan.days) {
        d.setDate(d.getDate() + this.app.detail.publicationTimespan.days);
        endDate = d;
      } else if (this.app.detail.publicationTimespan.months != null) {
        d.setMonth(d.getMonth() + this.app.detail.publicationTimespan.months);
        endDate = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      }
    }
    endDate.setHours(23, 59, 59, 999);
    let date = new Date(event.repetition.startDate);
    const nth = this.getWeekdayOccurrence(date);
    const day = date.getDay();
    const dates = [];
    dates.push(new Date(date));
    if (event.repetition.repetitionSize === 'day') {
      if (event.repetition.repetitionRhythm === '1') {
        date.setDate(date.getDate() + 1);
        while (date.getTime() < endDate.getTime()) {
          if (event.repetition.weekdays.includes(date.getDay().toString()) || event.repetition.weekdays.includes(date)) {
            dates.push(new Date(date));
          }
          date.setDate(date.getDate() + 1);
        }
      } else {
        date.setDate(date.getDate() + parseInt(event.repetition.repetitionRhythm));
        while (date.getTime() < endDate.getTime()) {
          dates.push(new Date(date));
          date.setDate(date.getDate() + parseInt(event.repetition.repetitionRhythm));
        }
      }
    } else if (event.repetition.repetitionSize === 'week') {
      const date = this.getMonday(event.repetition.startDate);
      while (date.getTime() < endDate.getTime()) {
        for (let i = 0; i < 7; i++) {
          if (date.getTime() < new Date(event.repetition.startDate).getTime()) {
            date.setDate(date.getDate() + 1);
            continue;
          }
          if (event.repetition.weekdays.includes(date.getDay().toString()) || event.repetition.weekdays.includes(date.getDay())) {
            dates.push(new Date(date));
          }
          date.setDate(date.getDate() + 1);
          if (i === 6) {
            date.setDate(date.getDate() + ((parseInt(event.repetition.repetitionRhythm) - 1) * 7));
          }
        }
      }
    } else if (event.repetition.repetitionSize === 'month') {
      if (event.repetition.repetitionLastDay) {
        while (date.getTime() < endDate.getTime()) {
          date.setDate(1);
          date.setMonth(date.getMonth() + parseInt(event.repetition.repetitionRhythm, 10) + 1);
          date.setDate(0);
          dates.push(new Date(date));
        }
      } else if (event.repetition.repetitionExact) {
        while (date.getTime() < endDate.getTime()) {
          dates.push(new Date(date));
          date.setDate(date.getDate() + this.getDaysInMonth(date));
        }
      } else {
        while (date.getTime() < endDate.getTime()) {
          dates.push(new Date(date));
          date = this.getNthWeekdayOfNextMonth(date, day, nth);
        }
      }
    } else if (event.repetition.repetitionSize === 'year') {
      if (event.repetition.repetitionExact) {
        while (date.getTime() < endDate.getTime()) {
          dates.push(new Date(date));
          date = new Date(date.getFullYear() + 1, date.getMonth(), date.getDate())
        }
      } else {
        while (date.getTime() < endDate.getTime()) {
          dates.push(new Date(date));
          for (let i = 0; i < 12; i++) {
            date = this.getNthWeekdayOfNextMonth(date, day, nth);
          }
        }
      }
    }
    return dates;
  }

  getMonday(date) {
    const currentDate = new Date(date);
    const dayOfWeek = currentDate.getDay();
    const diff = (dayOfWeek === 0) ? -6 : 1 - dayOfWeek;
    currentDate.setDate(currentDate.getDate() + diff);
    currentDate.setHours(0, 0, 0, 0);
    return currentDate;
  }

  getDaysInMonth(date) {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  }

  getWeekdayOccurrence(date) {
    const currentDate = new Date(date);
    currentDate.setDate(1);
    const firstOccurrence = 1 + ((date.getDay() - currentDate.getDay() + 7) % 7);
    return Math.floor((date.getDate() - firstOccurrence) / 7) + 1;
  }

  getNthWeekdayOfNextMonth(currentDate, targetWeekday, targetOccurrence) {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstOfNextMonth = new Date(year, month + 1, 1);
    const firstWeekday = firstOfNextMonth.getDay();
    const dayDiff = (targetWeekday - firstWeekday + 7) % 7;
    const firstTargetWeekday = 1 + dayDiff;
    const dayOfMonth = firstTargetWeekday + (targetOccurrence - 1) * 7;
    return new Date(year, month + 1, dayOfMonth);
  }

  getNextExecutionDate(schedules: any[]): Date | null {
    const now = new Date();
    if (this.archive) {
      let finalItem;
      for (const schedule of schedules) {
        if (!finalItem?.startDate || new Date(finalItem.startDate) < new Date(schedule.startDate)) {
          finalItem = schedule;
        }
      }
      return new Date(finalItem.endDate || finalItem.startDate);
    } else {
      for (const schedule of schedules) {
        const startDate = new Date(schedule.startDate);
        const endDate = new Date(schedule.endDate);

        if (now < startDate) {
          return startDate;
        } else if (now >= startDate && now <= endDate) {
          const nextExecution = new Date(now.toISOString().split('T')[0] + 'T00:00:00.000Z');
          if (now < nextExecution) {
            return nextExecution;
          } else {
            nextExecution.setUTCDate(nextExecution.getUTCDate() + 1);
            return nextExecution;
          }
        }
      }
    }
  }

  private checkValidDate(startDate, endDate) {
    return !((endDate && this.getNulledDate() > endDate) || (!endDate && this.getNulledDate() > startDate));
  }

  private getNulledDate(): number {
    return new Date().setHours(0, 0, 0, 0) / 1000;
  }

  private getDateUnix(dateString, isEndDate = false) {
    const res = this.getDate(dateString, isEndDate);
    return !!res ? res.getTime() / 1000 : null;
  }

  private getDate(dateString: string, isEndDate = false): Date | null {
    if (!dateString) {
      return null;
    }
    const [day, month, year] = /\d?\d\.\d?\d\.(\d{2}|\d{4})/.test(dateString) ? dateString.split('.').map(Number) : [];
    const date = day ? new Date(year, month - 1, day) : new Date(dateString);
    if (isEndDate) { date.setHours(23, 59, 59); }
    return date;
  }
}
